import { ES_MX_STOREFRONT_COPY } from "lib/esMxStorefrontCopy";
import { MX_STOREFRONT_COPY } from "lib/mxStorefrontCopy";

const DEFAULT_LOCALE = "en-US";

export const STOREFRONT_COPY_LOCALES = ["en-US", "es-MX"];

export const STOREFRONT_COPY_GROUPS = [
  { key: "common", label: "Common" },
  { key: "home", label: "Home" },
  { key: "catalog", label: "Catalog" },
  { key: "product", label: "Product Detail" },
  { key: "cart", label: "Cart" },
  { key: "checkout", label: "Checkout" },
  { key: "auth", label: "Auth" },
  { key: "portal", label: "Customer Portal" },
  { key: "orders", label: "Orders" },
  { key: "profile", label: "Profile" },
  { key: "addresses", label: "Addresses" },
  { key: "registeredUnits", label: "Registered Units" },
  { key: "coreReturn", label: "Returns" },
  { key: "warranty", label: "Warranty" },
  { key: "support", label: "Support" },
  { key: "legal", label: "Legal" },
];

export const DEFAULT_STOREFRONT_COPY = {
  [DEFAULT_LOCALE]: {
    common: {
      brandFallback: "Storefront",
      headerTagline: "Premium products and service",
      catalog: "Catalog",
      cart: "Cart",
      checkout: "Checkout",
      searchPlaceholder: "Search products...",
      browseCatalog: "Browse catalog",
      seeProcess: "See process",
      signIn: "Sign in",
      continue: "Continue",
      account: "Account",
      portal: "Portal",
      signOut: "Sign out",
      loading: "Loading...",
      unavailable: "Unavailable",
      supportName: "Support",
      footerRights: "All rights reserved.",
      back: "Back",
    },
    home: {
      eyebrow: "Premium catalog",
      heroTitle: "Find the right product with clear details.",
      heroSubtitle:
        "Browse current products, compare details, and place orders through a tenant-specific storefront.",
      featureOneTitle: "Process visibility",
      featureOneText:
        "Show customers how products are prepared, reviewed, and supported.",
      featureTwoTitle: "Product confidence",
      featureTwoText:
        "Explain the work behind each product before customers reach checkout.",
      featureThreeTitle: "Support after purchase",
      featureThreeText:
        "Connect the storefront process to account tools, returns, and support.",
      processTitle: "How the process works.",
      processSubtitle:
        "Use this area to explain how your team prepares products, verifies details, and supports customers after purchase.",
      processStepOneTitle: "Prepare",
      processStepOneText:
        "Describe how products are inspected, selected, built, or staged.",
      processStepTwoTitle: "Verify",
      processStepTwoText:
        "Explain how specifications, fitment, quality, or readiness are checked.",
      processStepThreeTitle: "Deliver",
      processStepThreeText:
        "Describe shipping, pickup, installation, or handoff expectations.",
      processStepFourTitle: "Support",
      processStepFourText:
        "Explain follow-up, returns, warranty, or account support after purchase.",
    },
    catalog: {
      title: "Find the right product.",
      subtitle:
        "Search, filter, and compare products available for this storefront.",
      emptyTitle: "No products found",
      emptyText: "Try adjusting your search or filters.",
      available: "Available",
      productStatusFallback: "Catalog item",
      skuPrefix: "SKU",
      priceQuote: "Quote",
      addToCart: "Add to cart",
      addedToCart: "Added to cart",
      inCart: "In cart",
    },
    product: {
      backToCatalog: "Back to catalog",
      details: "Details",
      unitDetails: "Unit details",
      specifications: "Specifications",
      installationNotes: "Installation notes",
      technicalDetails: "Product details",
      fitmentYears: "Fitment",
      addToCart: "Add to cart",
      checkout: "Checkout",
      alreadyInCart: "already in cart",
      coreCharge: "Core charge",
      productFallback: "Product",
      footerCatalog: "Catalog",
    },
    cart: {
      title: "Cart",
      emptyTitle: "Your cart is empty",
      emptyText: "Add products from the catalog to begin an order.",
      total: "Cart total",
      checkout: "Checkout",
      signInRequired: "Checkout will require signing in to a customer account.",
      refundableDeposit: "Deposit is refundable after return and approval.",
    },
    checkout: {
      title: "Checkout",
      shipping: "Shipping",
      payment: "Payment",
      placeOrder: "Place order",
      needInvoice: "Need invoice details?",
      requiredFields: "Required fields are missing.",
    },
    auth: {
      signInTitle: "Sign in",
      registerTitle: "Create account",
      customerAccount: "Customer account",
      distributorAccount: "Distributor account",
      email: "Email",
      password: "Password",
    },
    portal: {
      customerPortal: "Customer portal",
      distributorPortal: "Distributor portal",
      accountTools: "Account tools",
      distributorTools: "Distributor tools",
      accountToolsDescription:
        "Orders, registered items, profile, invoices, and addresses.",
      distributorToolsDescription:
        "Orders, registered items, profile, and addresses.",
      accountHome: "Account",
      registeredUnits: "Registered Items",
      coreReturns: "Core returns",
      returnCore: "Return core",
      open: "Open",
      viewOnlyNotice:
        "Some actions are hidden because your membership is view-only.",
      orders: "Orders",
      profile: "Profile",
      profileBilling: "Profile / Billing",
      addresses: "Addresses",
      deleteAccount: "Delete Account",
      browseCatalog: "Browse catalog",
    },
    orders: {
      title: "Orders",
      subtitle: "Track orders and documents.",
      recentOrders: "Recent orders",
      noOrders: "No orders yet.",
      viewAll: "View all orders",
    },
    profile: {
      title: "Profile",
      subtitle: "Account and invoice preferences.",
      invoicePreferences: "Invoice preferences",
      invoiceDescription: "Invoice and tax information for checkout.",
      missingTaxFields: "Missing required tax fields for checkout:",
      deleteRequestText:
        "This sends a verified deletion request. We will review it and follow up by email.",
    },
    addresses: {
      title: "Addresses",
      subtitle: "Manage shipping and billing addresses.",
    },
    registeredUnits: {
      title: "Registered Items",
      subtitle: "Track registered products, returns, and warranty requests.",
      fallbackName: "Registered Item",
      open: "Open registered items",
      scanTitle: "Scan",
    },
    coreReturn: {
      title: "Return core",
      subtitle: "Submit a return request for this item.",
      wizardSubtitle: "Confirm, pack, then print your prepaid label.",
      hubTitle: "Core returns",
      hubSubtitle: "Return owed cores with a prepaid label in three easy steps.",
      hubEmpty:
        "You do not have any cores owed or active core returns right now.",
      owedSection: "Cores you owe",
      activeSection: "In progress",
      noOwed: "No cores are currently owed.",
      openReturn: "Open return",
      awaitingLabel: "Awaiting label",
      backToHub: "All core returns",
      creditLabel: "Refundable credit",
      deadlineLabel: "Deadline",
      pendingQty: "Pending qty",
      serialPending: "Serial pending",
      loading: "Loading core returns...",
      loadError: "Failed to load core returns.",
      notFound: "This valve body could not be loaded.",
      membershipBlocked: "Your membership does not allow starting core returns.",
      confirmTitle: "Confirm this core",
      confirmSubtitle:
        "You are returning this valve body core with a prepaid VBS label.",
      packTitle: "Pack the core",
      packSubtitle: "Follow these steps so oil does not spill in transit.",
      packComplete: "Packaging confirmed. Choose where to drop off your core.",
      continueToLabel: "Continue to label",
      saving: "Saving...",
      startError: "Failed to start core return.",
      notes: "Notes",
      photos: "Return photos (optional)",
      checklistRequired: "Confirm all packaging items before continuing.",
      shipTitle: "Choose drop-off option",
      shipSubtitle:
        "Prepaid options — no charge to you. Pick a carrier, then a nearby drop-off for your prepaid label.",
      labelReadyTitle: "Your label is ready",
      labelReadySubtitle:
        "Print the prepaid label and drop off the packaged core.",
      labelReadyMessage: "Your prepaid return label is ready.",
      printLabel: "Print label",
      trackReturn: "Track return",
      refreshOptions: "Refresh options",
      retryOptions: "Retry shipping options",
      loadingOptions: "Loading prepaid options...",
      optionsError: "Unable to load prepaid shipping options.",
      noOptions: "No prepaid drop-off options are available right now.",
      bookError: "Unable to generate the return label.",
      generatingLabel: "Generating label...",
      modeDropoff: "Print label and drop off",
      modePickup: "Pickup + prepaid label",
      deliveryDays: "{days} day estimate",
      dropOffTitle: "Nearby drop-off locations",
      dropOffTitleForCarrier: "{carrier} drop-off locations",
      dropOffEmpty:
        "No nearby drop-off locations were returned for this carrier. Use the carrier’s website locator after printing your label.",
      openMap: "Open map",
      timelineTitle: "Return progress",
      refundText:
        "Once the return is received, inspected, and approved, the eligible amount is refunded.",
      sealedLabel: "The item is sealed in the provided packaging.",
      checklist: {
        oil: "The box is not leaking or dripping oil.",
        box: "The return uses the original or old box.",
      },
      steps: {
        confirm: "1. Confirm",
        pack: "2. Pack",
        ship: "3. Print label",
      },
      instructions: {
        oilTitle: "Drain all oil",
        oilBody:
          "Drain the core completely. Wipe residue so the box is not dripping or leaking oil.",
        bagTitle: "Seal in a plastic bag",
        bagBody:
          "Place the core in the provided plastic bag and seal it before boxing.",
        boxTitle: "Use the original box",
        boxBody:
          "Pack the bagged core in the original or old box so it ships securely.",
      },
      status: {
        owed: "Core due",
        started: "Return started",
        labelReady: "Label ready",
        inTransit: "In transit",
        received: "Received",
        inspection: "Warehouse inspection",
        approved: "Approved",
        forfeited: "Forfeited",
        none: "No active return",
      },
      timeline: {
        started: "Return started",
        label: "Label ready",
        transit: "In transit",
        received: "Received / inspection",
        approved: "Approved",
      },
    },
    warranty: {
      title: "Warranty",
      subtitle: "Start and track warranty requests.",
      activeClaim: "An active claim is already open.",
      returnShipping: "Return shipping",
      inspectionUpdates:
        "Inspection updates appear here after the returned item is received.",
    },
    support: {
      title: "Support",
      subtitle: "Message support and share claim images.",
      staffName: "Support",
      you: "You",
    },
    legal: {
      privacyTitle: "Privacy Policy",
      deleteTitle: "Delete Account",
      privacyIntro:
        "This policy explains how this storefront collects, uses, shares, and protects customer information.",
      deleteIntro:
        "You can request deletion of your customer or distributor account from the account portal.",
      appInstruction: "Sign in to your customer or distributor account.",
      retainedRecords:
        "Order, return, warranty, support, and fraud-prevention records.",
      verificationText:
        "We may contact you from an official company email address if more verification is needed.",
    },
  },
  "es-MX": ES_MX_STOREFRONT_COPY,
};

export const VBS_STOREFRONT_COPY = {
  "en-US": {
    common: {
      brandFallback: "Valve Body Specialties USA",
      headerTagline: "Precision remanufacturing",
      searchPlaceholder: "Search valve bodies...",
      home: "Home",
      register: "Register",
      utilityStrip: "Valve bodies · Cores · Warranty support",
      footerBlurb:
        "Remanufactured valve bodies for transmission shops — measured, calibrated, and backed by warranty registration in your portal.",
      shopSection: "SHOP",
      legalSection: "LEGAL",
      privacy: "Privacy",
      deleteAccount: "Delete account",
      menu: "Menu",
      add: "Add",
      details: "Details →",
      skuTbd: "SKU TBD",
      requestQuote: "Request quote",
      loadingInventory: "Loading inventory…",
      listingCount: "{count} listing(s)",
      loadingCatalog: "Loading catalog…",
      allTransmissions: "All transmissions",
      productCatalog: "Product catalog",
    },
    home: {
      eyebrow: "Premium remanufactured valve bodies",
      heroTitle: "Clean shifts start with controlled hydraulics.",
      heroSubtitle:
        "Valve bodies rebuilt with measured wear correction, precise calibration, and a workflow built for repeatable results.",
      primaryCta: "Shop products",
      secondaryCta: "Sign in",
      myAccount: "My account",
      openAccount: "Open account",
      goToPortal: "Go to portal",
      createAccount: "Create account",
      trustQc: "Documented calibration on every unit",
      trustCore: "Core return program with clear tracking",
      trustWarranty: "Register valve bodies in your portal",
      trustSupport: "Shop-direct technical assistance",
      trustStatQc: "QC",
      trustStatCore: "CORE",
      trustStatWarranty: "Warranty",
      trustStatSupport: "Support",
      featuredEyebrow: "In stock & ready",
      featuredTitle: "Shop valve bodies",
      featuredLink: "Full catalog",
      loadingProducts: "Loading products…",
      ctaBandTitle: "Built for transmission shops",
      ctaBandBody:
        "Order parts, register units, manage cores, and open warranty claims — all from one account.",
      featureOneTitle: "Precision first",
      featureOneText: "Component-level inspection before assembly.",
      featureTwoTitle: "Built for installers",
      featureTwoText: "Straightforward catalog flow and account ordering.",
      featureThreeTitle: "Consistent output",
      featureThreeText: "A repeatable process for repeatable shift quality.",
      processTitle: "A disciplined rebuild flow.",
      processSubtitle:
        "The process is designed to remove guesswork and keep the product focused on hydraulic control.",
      processStepOneTitle: "Disassemble",
      processStepOneText:
        "Every channel, bore, and spool is opened for inspection.",
      processStepTwoTitle: "Measure",
      processStepTwoText:
        "Critical wear points are checked before any component moves forward.",
      processStepThreeTitle: "Restore",
      processStepThreeText:
        "Bores and circuits are corrected for consistent hydraulic behavior.",
      processStepFourTitle: "Validate",
      processStepFourText:
        "Finished units are verified for fit, pressure response, and durability.",
    },
    catalog: {
      title: "Find the right valve body.",
      subtitle: "Search available valve bodies with current tenant pricing.",
      empty: "No products match your search.",
      productStatusFallback: "Catalog item",
    },
    product: {
      backToProducts: "Back to products",
      details: "Details",
      unitDetails: "Unit details",
      specifications: "Specifications",
      installationNotes: "Installation notes",
      technicalDetails: "Product details",
      fitmentYears: "Fitment",
      signInToOrder: "Sign in to order",
      buyNow: "Buy now",
      unitPrice: "Unit price",
      coreDeposit: "core deposit (refundable)",
      contactPricing: "Contact us for pricing on this item.",
      loadingProduct: "Loading product…",
      notFound: "Product not found",
      addedNotice: "{count} added to cart",
    },
    portal: {
      registeredUnits: "Valve Bodies",
      coreReturns: "Core returns",
      returnCore: "Return core",
      accountToolsDescription:
        "Orders, valve bodies, profile, invoices, and addresses.",
      distributorToolsDescription:
        "Orders, sold units, profile, and addresses.",
    },
    registeredUnits: {
      title: "Valve Bodies",
      subtitle: "Register sold units, return cores, and start warranty claims.",
      fallbackName: "Valve Body",
      open: "Open valve bodies",
      scanTitle: "Scan",
    },
    coreReturn: {
      hubTitle: "Core returns",
      hubSubtitle: "Return owed cores with a prepaid label in three easy steps.",
      wizardSubtitle: "Confirm, pack, then print your prepaid label.",
      refundText:
        "Return your rebuildable core. Once VBS receives, inspects, and approves it, this amount is refunded back to you.",
      sealedLabel: "The valve body is sealed in the provided plastic bag.",
    },
    warranty: {
      inspectionUpdates:
        "Inspection updates appear here after the returned unit is received by VBS.",
      returnShipping: "Return shipping (paid by VBS)",
    },
    support: {
      staffName: "VBS Support",
      subtitle: "Message VBS support and share claim images.",
    },
    legal: {
      privacyIntro:
        "This policy explains how Valve Body Specialties collects, uses, shares, and protects customer information.",
      appInstruction:
        "Open the VBS native app and sign in to your customer or distributor account.",
      verificationText:
        "We may contact you from a VBS email address if more verification is needed.",
    },
  },
  "es-MX": {
    common: {
      brandFallback: "Valve Body Specialties USA",
      headerTagline: "Remanufactura de precisión",
      searchPlaceholder: "Buscar valve bodies...",
    },
    home: {
      eyebrow: "Valve bodies remanufacturados premium",
      heroTitle: "Cambios limpios empiezan con hidráulica controlada.",
      heroSubtitle:
        "Valve bodies reconstruidos con corrección de desgaste, calibración precisa y un flujo repetible.",
    },
  },
};

const TENANT_STOREFRONT_COPY = {
  vbsus: VBS_STOREFRONT_COPY,
  mx: MX_STOREFRONT_COPY,
};

function isPlainObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

export function deepMergeCopy(...sources) {
  const out = {};
  sources.forEach((source) => {
    if (!isPlainObject(source)) return;
    Object.entries(source).forEach(([key, value]) => {
      if (isPlainObject(value)) {
        out[key] = deepMergeCopy(out[key], value);
      } else if (value != null && value !== "") {
        out[key] = value;
      }
    });
  });
  return out;
}

export function normalizeStorefrontLocale(value) {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (raw === "es" || raw.startsWith("es-")) return "es-MX";
  return DEFAULT_LOCALE;
}

export function storefrontCopyForTenant(tenant, locale) {
  const tenantKey = String(tenant?.companyKey || tenant?.key || "")
    .trim()
    .toLowerCase();
  const tenantBundle = TENANT_STOREFRONT_COPY[tenantKey] || null;
  const normalizedLocale = normalizeStorefrontLocale(
    locale || tenant?.defaultLocale || (tenantKey === "mx" ? "es-MX" : ""),
  );
  return deepMergeCopy(
    DEFAULT_STOREFRONT_COPY[DEFAULT_LOCALE],
    normalizedLocale !== DEFAULT_LOCALE
      ? DEFAULT_STOREFRONT_COPY[normalizedLocale]
      : null,
    tenantBundle?.[DEFAULT_LOCALE],
    normalizedLocale !== DEFAULT_LOCALE
      ? tenantBundle?.[normalizedLocale]
      : null,
    tenant?.storefrontCopy?.[DEFAULT_LOCALE],
    normalizedLocale !== DEFAULT_LOCALE
      ? tenant?.storefrontCopy?.[normalizedLocale]
      : null,
  );
}

export function getStorefrontCopyValue(copy, path, fallback = "") {
  const value = String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce(
      (acc, key) => (acc && acc[key] != null ? acc[key] : undefined),
      copy
    );
  if (value == null || value === "") return fallback;
  return String(value);
}
